import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import { cn } from "@/zapi-frontend/shared/lib/utils";

const CLOSE_MS = 300;
const MIN_HEIGHT = 240;

function useRendered(open: boolean) {
  const [rendered, setRendered] = useState(open);
  if (open && !rendered) setRendered(true);
  useEffect(() => {
    if (open) return;
    const timer = window.setTimeout(() => setRendered(false), CLOSE_MS);
    return () => window.clearTimeout(timer);
  }, [open]);
  return rendered;
}

function isEditable(target: EventTarget | null) {
  return target instanceof HTMLElement && (target.isContentEditable || target.matches("input, textarea, select"));
}

function useVisibleHeight(enabled: boolean) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>();
  const [headerHeight, setHeaderHeight] = useState<number>();

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    const body = anchor?.closest<HTMLElement>("[data-slot=chapter-widget-body]");
    const header = body?.parentElement?.querySelector<HTMLElement>(":scope > [data-slot=widget-card-header]");
    if (!enabled || !anchor || !body) return;
    let frame = 0;
    const measure = () => {
      frame = 0;
      setHeaderHeight(header?.offsetHeight ?? 0);
      const top = anchor.getBoundingClientRect().top;
      const bottom = Math.min(window.innerHeight, body.getBoundingClientRect().bottom);
      setHeight(Math.max(MIN_HEIGHT, Math.round(bottom - top)));
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    const observer = new ResizeObserver(schedule);
    observer.observe(body);
    if (header) observer.observe(header);
    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      observer.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [enabled]);

  return { anchorRef, height, headerHeight };
}

export function ChapterToolPanel({
  open,
  pinned,
  onClose,
  children
}: {
  open: boolean;
  pinned: boolean;
  onClose: () => void;
  children: ReactNode;
}) {
  const rendered = useRendered(open);
  const { anchorRef, height, headerHeight } = useVisibleHeight(rendered && pinned);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented || event.isComposing || event.keyCode === 229) return;
      if (!isEditable(event.target)) onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!rendered) return null;
  return (
    <aside
      data-slot="chapter-tool-panel"
      data-state={open ? "open" : "closed"}
      className={cn(
        "absolute inset-y-0 end-0 z-20 w-[22rem] max-w-[calc(100cqw-2rem)] overflow-clip rounded-ee-2xl border-s border-foreground/10 bg-card shadow-[-16px_0_32px_-16px_rgb(0_0_0/0.18)]",
        "transition-[translate,opacity] duration-300 ease-out starting:translate-x-8 starting:opacity-0 rtl:starting:-translate-x-8",
        "data-[state=closed]:translate-x-8 data-[state=closed]:opacity-0 rtl:data-[state=closed]:-translate-x-8"
      )}
    >
      <div
        ref={anchorRef}
        style={
          pinned
            ? {
                height,
                top: headerHeight === undefined ? undefined : `calc(var(--widget-sticky-top) + ${headerHeight}px)`
              }
            : undefined
        }
        className={cn("flex", pinned ? "sticky top-[calc(var(--widget-sticky-top)+3.5rem)]" : "h-full")}
      >
        {children}
      </div>
    </aside>
  );
}
